/* eslint-disable @typescript-eslint/no-explicit-any */
import { notificationController } from '@app/controllers/notificationController';

type Translator = (key: string) => string;

export type UiErrorKind =
  | 'canceled'
  | 'offline'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'rateLimit'
  | 'maintenance'
  | 'validation'
  | 'conflict'
  | 'server'
  | 'unexpected';

interface HandleApiErrorOptions {
  defaultMessage?: string;
  silent?: boolean;
  asWarning?: boolean;
}

const backendMsg = (data: any): string => {
  if (!data) return '';
  if (typeof data?.error?.message === 'string') return data.error.message;
  if (typeof data.message === 'string') return data.message;
  if (typeof data.mensagem === 'string') return data.mensagem;
  if (typeof data.error === 'string') return data.error;
  if (Array.isArray(data.errors) && data.errors[0]?.message)
    return data.errors[0].message;
  return '';
};

const pickValidationKey = (bm: string) => {
  if (/cpf/i.test(bm)) return 'cashBack.cpfInvalid';
  if (/email/i.test(bm)) return 'cashBack.emailInvalid';
  if (/vencimento|due date/i.test(bm)) return 'cashBack.invalidDueDate';
  if (/valor|amount/i.test(bm)) return 'cashBack.invalidAmount';
  return null;
};

export function getUiErrorKind(err: any): UiErrorKind {
  const status = err?.status ?? err?.response?.status;
  const msg = String(err?.message || '').toLowerCase();
  const code = err?.code ?? err?.options?.code;

  const canceled =
    err?.name === 'AbortError' ||
    code === 'ERR_CANCELED' ||
    msg.includes('canceled') ||
    msg.includes('aborted');
  if (canceled) return 'canceled';

  const timeout = code === 'ECONNABORTED' || msg.includes('timeout');
  if (timeout) return 'timeout';

  const isNetwork =
    !status &&
    (code === 'ERR_NETWORK' ||
      msg.includes('network error') ||
      msg.includes('failed to fetch'));
  if (isNetwork) return 'offline';

  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 429) return 'rateLimit';
  if (status === 503) return 'maintenance';
  if (status === 400 || status === 422) return 'validation';
  if (status === 409) return 'conflict';
  if (typeof status === 'number' && status >= 500) return 'server';
  return 'unexpected';
}

export function handleApiError(
  e: unknown,
  t: Translator,
  options: HandleApiErrorOptions = {}
) {
  const err = e as any;
  if (process.env.NODE_ENV !== 'production') console.error('[api]', err);

  const kind = getUiErrorKind(err);
  if (kind === 'canceled' || options.silent) return;

  const data = err?.response?.data || {};
  const bm = backendMsg(data);

  const msgKeyByKind: Record<
    Exclude<UiErrorKind, 'validation' | 'conflict' | 'unexpected' | 'server'>,
    string
  > = {
    offline: 'common.networkError',
    timeout: 'common.timeoutError',
    unauthorized: 'common.unauthorized',
    forbidden: 'common.forbidden',
    notFound: 'common.notFound',
    rateLimit: 'common.tooManyRequests',
    maintenance: 'common.maintenance',
    canceled: 'common.error',
  };

  const show = options.asWarning
    ? notificationController.warning
    : notificationController.error;

  if (kind in msgKeyByKind) {
    return show({
      message: t(msgKeyByKind[kind as keyof typeof msgKeyByKind]),
    });
  }

  if (kind === 'validation') {
    const k = pickValidationKey(bm);
    return show({
      message: t(k ?? 'common.validationError'),
    });
  }

  if (kind === 'conflict') {
    if (/duplicate|já existe/i.test(bm)) {
      return notificationController.warning({
        message: t('cashBack.duplicateCharge'),
      });
    }

    if (/already paid|já paga/i.test(bm)) {
      return notificationController.warning({
        message: t('cashBack.alreadyPaid'),
      });
    }

    return show({
      message: t('cashBack.conflict'),
    });
  }

  const fallback = options.defaultMessage || bm || t('common.error');
  return show({ message: fallback });
}
